import { defineStore, storeToRefs } from 'pinia';
import { ref, watch } from 'vue';
import { useCookiesStore } from '@/stores/cookies.store';
import { useAppConfigStore } from '@stores/appConfig.store';
import { useAuthStore } from '@stores/auth.store';
import { useTranslationCache } from '@/stores/translationCache.store';
import useHttp from '@/utils/httpClient';
import { differenceInDays, format } from 'date-fns';
import { useBookingQueryStore } from '@stores/bookingQuery.store';

const getCookieValue = (key) => {
  const cookie = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${key}=`))
    ?.split('=')[1];
  return cookie ? cookie : null;
};

const http = useHttp();

const setCookie = (key, value, expirationDays = null) => {
  let cookieString = `${key}=${value}; path=/`;
  if (expirationDays) {
    const date = new Date();
    date.setTime(date.getTime() + expirationDays * 24 * 60 * 60 * 1000);
    cookieString += `; expires=${date.toUTCString()}`;
  }
  document.cookie = cookieString;
};

const createCookieKey = (prefix, hotelId, trigger) =>
  `${prefix}_${hotelId}${trigger ? `_${trigger}` : ''}`;

export const useShoppingRecoveryStore = defineStore('shoppingRecovery', () => {
  const cookiesStore = useCookiesStore();
  const { userCookies } = storeToRefs(cookiesStore);
  const appConfigStore = useAppConfigStore();
  const { applicationSetup, setupLoaded } = storeToRefs(appConfigStore);
  const authStore = useAuthStore();
  const { apikey, channelKey } = storeToRefs(authStore);
  const translationCache = useTranslationCache();
  const { labelsResponse } = storeToRefs(translationCache);

  const bookingQueryStore = useBookingQueryStore();
  const { query } = storeToRefs(bookingQueryStore);

  const SHOPPING_VISITS_COOKIE = 'GCShoppingVisits';
  const SHOPPING_RECOVERY_COOKIE = 'GCShoppingRecovery';

  const hotelId = ref(null);
  const visitCount = ref(0);
  const shoppingRecoveryList = ref([]);
  const hasIncrementedVisits = ref(false);
  const activeRecoveries = ref(new Map());
  const shouldShowRecovery = ref(new Map());
  const isInitialized = ref(false);
  const lastActivityTime = ref(Date.now());

  const activityTimers = ref(new Map());
  const mouseLeaveHandlers = ref(new Map());

  const handleVisits = () => {
    const cookieKey = createCookieKey(
      SHOPPING_VISITS_COOKIE,
      hotelId.value,
      '',
    );
    const cookieValue = getCookieValue(cookieKey);
    visitCount.value = cookieValue ? parseInt(cookieValue) + 1 : 1;
    setCookie(cookieKey, visitCount.value, 30);
    hasIncrementedVisits.value = true;
  };

  const getRecoveryType = (recovery) => {
    if (recovery.promoId === '-1') return 'modal';
    if (recovery.promoId === '0') return 'newsletter';
    return 'promotion';
  };

  const createShoppingRecoveries = () => {
    const setupData = applicationSetup.value.shopping_recovery || [];
    const labelsData = labelsResponse.value?.shopping_recovery_labels || {};

    // Filter and merge: only items that exist in both services
    const recoveryList = setupData
      .filter((setupItem) => labelsData[setupItem.id])
      .map((setupItem) => ({
        ...setupItem,
        ...labelsData[setupItem.id],
      }));

    shoppingRecoveryList.value = recoveryList
      .filter(
        (recovery) =>
          !getCookieValue(
            createCookieKey(
              SHOPPING_RECOVERY_COOKIE,
              hotelId.value,
              recovery.trigger,
            ),
          ),
      )
      .map((recovery) => ({
        ...recovery,
        type: getRecoveryType(recovery),
      }));
  };

  const handleMouseLeave = (e, recovery) => {
    if (e.clientY <= 0) {
      showRecovery(recovery);
      removeMouseLeaveListener(recovery);
    }
  };

  const removeMouseLeaveListener = (recovery) => {
    const handler = mouseLeaveHandlers.value.get(recovery.trigger);
    if (handler) {
      document.removeEventListener('mouseout', handler);
      mouseLeaveHandlers.value.delete(recovery.trigger);
    }
    activeRecoveries.value.delete(recovery.trigger);
  };

  const handleUserActivity = () => {
    lastActivityTime.value = Date.now();
  };

  const setupActivityListeners = () => {
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
    ];
    activityEvents.forEach((event) =>
      document.addEventListener(event, handleUserActivity),
    );
  };

  const removeActivityListeners = () => {
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
    ];
    activityEvents.forEach((event) =>
      document.removeEventListener(event, handleUserActivity),
    );
  };

  const setupRecoveryBehavior = (recovery) => {
    if (!recovery || !userCookies.value.functionalityOrPersonalizationCookies)
      return;

    const cookieKey = createCookieKey(
      SHOPPING_RECOVERY_COOKIE,
      hotelId.value,
      recovery.trigger,
    );
    if (getCookieValue(cookieKey)) return;

    activeRecoveries.value.set(recovery.trigger, recovery);

    switch (recovery.trigger) {
      case 'LANDING':
        if (visitCount.value >= recovery.frequency) {
          showRecovery(recovery);
        }
        break;
      case 'PERMANENCY':
        setTimeout(
          () => showRecovery(recovery),
          recovery.frequency * 60 * 1000,
        );
        break;
      case 'EXIT':
        if (
          visitCount.value >= recovery.frequency &&
          !mouseLeaveHandlers.value.has(recovery.trigger)
        ) {
          const handler = (e) => handleMouseLeave(e, recovery);
          mouseLeaveHandlers.value.set(recovery.trigger, handler);
          document.addEventListener('mouseout', handler);
        }
        break;
      case 'INACTIVITY': {
        setupActivityListeners();
        const checkInactivity = () => {
          const inactiveTime =
            (Date.now() - lastActivityTime.value) / (1000 * 60);

          if (inactiveTime >= recovery.frequency) {
            showRecovery(recovery);
            clearInterval(activityTimers.value.get(recovery.trigger));
            activityTimers.value.delete(recovery.trigger);
          }
        };
        activityTimers.value.set(
          recovery.trigger,
          setInterval(checkInactivity, 5000),
        );
        break;
      }
    }
  };

  const cleanupRecoveryBehavior = (recovery) => {
    if (!recovery) return;

    if (recovery.trigger === 'EXIT') {
      removeMouseLeaveListener(recovery);
    }

    if (['INACTIVITY', 'ACTIVITY'].includes(recovery.trigger)) {
      const timer = activityTimers.value.get(recovery.trigger);
      if (timer) {
        clearInterval(timer);
        activityTimers.value.delete(recovery.trigger);
      }
    }

    activeRecoveries.value.delete(recovery.trigger);
    shouldShowRecovery.value.delete(recovery.trigger);
  };

  const cleanupAllRecoveries = () => {
    activeRecoveries.value.forEach(cleanupRecoveryBehavior);
    activeRecoveries.value.clear();
    shouldShowRecovery.value.clear();
    mouseLeaveHandlers.value.clear();
    activityTimers.value.clear();
    removeActivityListeners();
    isInitialized.value = false;
  };

  const showRecovery = (recovery) => {
    if (!recovery || !userCookies.value.functionalityOrPersonalizationCookies)
      return false;

    const cookieKey = createCookieKey(
      SHOPPING_RECOVERY_COOKIE,
      hotelId.value,
      recovery.trigger,
    );
    setCookie(cookieKey, 'true');
    shouldShowRecovery.value.set(recovery.trigger, true);
    return true;
  };

  const initializeStore = () => {
    if (
      !setupLoaded.value ||
      !userCookies.value.functionalityOrPersonalizationCookies
    ) {
      cleanupAllRecoveries();
      return;
    }

    hotelId.value = applicationSetup.value.hotel_id;
    if (!hasIncrementedVisits.value) {
      handleVisits();
    }
    createShoppingRecoveries();
    isInitialized.value = true;
  };

  const sendRecoveryPromotionForm = async (recoveryData) => {
    try {
      let url = `?apikey=${apikey.value}`;
      if (channelKey.value) {
        url += `&channelKey=${channelKey.value}`;
      }
      url = `${applicationSetup.value.hotel_id}/shopping_recovery_email` + url;

      const nrNights = differenceInDays(
        query.value.endDate,
        query.value.startDate,
      );
      let requestData = {
        action: 'submitShoppingRecovery',
        l: applicationSetup.value.language,
        promoId: recoveryData.promoId,
        frequency: recoveryData.frequency,
        context: recoveryData.context || 'BOOKINGENGINE',
        checkin: format(query.value.startDate, 'yyyy-MM-dd'),
        checkout: format(query.value.endDate, 'yyyy-MM-dd'),
        nights: nrNights,
        email: recoveryData.email,
      };

      if (recoveryData.name) {
        requestData.name = recoveryData.name;
      }

      if (recoveryData.country_code) {
        requestData.country = recoveryData.country_code;
      }

      let lastResponse = null;
      for (const occupancy of query.value.occupancy) {
        requestData.rooms = 1;
        requestData.adults = occupancy.nrAdults;
        requestData.children = occupancy.nrChildren;
        try {
          const response = await http.post(url, requestData);
          lastResponse = response;
        } catch (error) {
          console.log('submit failed:', error);
          throw error;
        }
      }

      return lastResponse;
    } catch (error) {
      console.log('Error sending recovery promotion form:', error);
      throw error;
    }
  };

  watch(
    () => [
      setupLoaded.value,
      labelsResponse.value,
      userCookies.value.functionalityOrPersonalizationCookies,
    ],
    initializeStore,
    { immediate: true },
  );

  return {
    SHOPPING_RECOVERY_COOKIE,
    hotelId,
    visitCount,
    handleVisits,
    hasIncrementedVisits,
    shoppingRecoveryList,
    setupRecoveryBehavior,
    cleanupRecoveryBehavior,
    showRecovery,
    activeRecoveries,
    shouldShowRecovery,
    isInitialized,
    initializeStore,
    sendRecoveryPromotionForm,
  };
});
